import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import withTheme from '@material-ui/styles/withTheme';
import {
    Grid,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { useHistory } from 'react-router';
import { Form, Formik } from 'formik';
import { useNotify } from '../../../../hooks/useNotify';
import { useSwaggerClient, doSwaggerCall } from '../../../../hooks/useSwaggerClient';
import FormSection from '../../../../components/atom/FormSection';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { ErrorIcon } from '../../../../icons';
import { RouterPrompt } from '../../../../components/atom/RouterPrompt';
import FormikInput from '../../../../components/atom/FormikInput';
import CapabilityEditor from '../../../../components/atom/CapabilityEditor';
import { refreshUserCapabilities } from '../../../../actions/user';
import { useDispatch } from 'react-redux';

function UserGroupEditPage({ clientId, theme }) {
    const { groupId } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();
    const [userGroup, loading, error] = useSwaggerClient('usergroups', 'getUserGroupById', {
        userGroupId: parseInt(groupId, 10)
    });
    const [submitUserGroup, setSubmitUserGroup] = useState(false);
    const dispatch = useDispatch();

    const onSubmit = (values, { setSubmitting }) => {
        setSubmitting(true);
        doSwaggerCall('usergroups', 'updateUserGroup', {
            userGroup: values,
            userGroupId: parseInt(groupId, 10)
        })
            .then(() => {
                notifySuccess('userGroup modified!');
                setSubmitting(false);
                setSubmitUserGroup(true);
                dispatch(refreshUserCapabilities());
                history.push(`/admin/client/${clientId}/usergroups`);
            })
            .catch(() => {
                notifyError('Could not modify userGroup!');
                setSubmitting(false);
            });
    };

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }
    if (loading) {
        return (
            <Grid container justifyContent="center">
                <CircularProgress />
            </Grid>
        );
    }
    return (
        <Formik onSubmit={onSubmit} initialValues={userGroup}>
            {({ isSubmitting, values, dirty, setFieldValue }) => (
                <>
                    <RouterPrompt when={!!dirty && !submitUserGroup} />
                    <Form>
                        <Grid container spacing={2}>
                            <AdminHeader title={`User group: ${userGroup?.name}`} />
                            {!!error && (
                                <Grid
                                    item
                                    container
                                    spacing={2}
                                    xs={12}
                                    style={{
                                        color: theme.palette.error.main
                                    }}
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <ErrorIcon color="error" />
                                    </Grid>
                                    <Grid item>
                                        <span>{error.response.body.message}</span>
                                    </Grid>
                                </Grid>
                            )}
                            <FormSection title="Generic information">
                                <FormikInput name="name" label="Name*" xs={12} />
                            </FormSection>
                            <FormSection title="Users in group">
                                {userGroup.users?.length > 0 ? (
                                    <Table
                                        style={{ backgroundColor: 'white' }}
                                        size="small"
                                        spacing={2}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Firstname</TableCell>
                                                <TableCell>Lastname</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {userGroup.users.map((user, index) => (
                                                <TableRow
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    key={index}
                                                >
                                                    <TableCell>{user.email}</TableCell>
                                                    <TableCell>{user.firstname}</TableCell>
                                                    <TableCell>{user.lastname}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Grid item xs={6}>
                                        <h4>Group without user.</h4>
                                    </Grid>
                                )}
                            </FormSection>
                            <FormSection title="Affiliate ID and IATA/CLIA numbers">
                                <FormikInput name="affiliateId" label="Affiliate ID" xs={6} />
                                <FormikInput
                                    name="iataCliaNumber"
                                    label="IATA/CLIA Number"
                                    xs={6}
                                />
                            </FormSection>
                            <CapabilityEditor
                                value={values.capabilities}
                                onChange={newCapabilities =>
                                    setFieldValue('capabilities', newCapabilities)
                                }
                            />
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={isSubmitting || !values.name}
                                >
                                    Save
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() =>
                                        history.push(`/admin/client/${clientId}/usergroups`)
                                    }
                                >
                                    Back
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                </>
            )}
        </Formik>
    );
}

export default withTheme(UserGroupEditPage);
